import * as React from 'react'
import { ArrowRight, Image as ImageIcon, Loader2, Trash2 } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { apiFetch, apiJson } from '@/lib/api'
import type { StoryboardDetail, StoryboardSummary } from '@/types'

interface StoryboardDetailModalProps {
  storyboard: StoryboardSummary | null
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called after a successful delete so the parent list refreshes. */
  onDeleted: () => void
  /** Routes to the Create-Ad wizard pre-loaded with this storyboard. */
  onUseInCreate: (storyboard: StoryboardSummary) => void
}

/**
 * Detail modal for a Storyboard parent row. Lazy-loads the full plan +
 * linked asset list when opened (the list endpoint only returns a
 * summary). Renders the title / brief / palette / panel count / linked
 * assets + a delete button (deletes the parent row only — assets stay).
 */
export function StoryboardDetailModal({
  storyboard,
  open,
  onOpenChange,
  onDeleted,
  onUseInCreate,
}: StoryboardDetailModalProps) {
  const [detail, setDetail] = React.useState<StoryboardDetail | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [deleting, setDeleting] = React.useState(false)

  React.useEffect(() => {
    if (!open || !storyboard) {
      setDetail(null)
      setError(null)
      return
    }
    let cancelled = false
    setLoading(true)
    ;(async () => {
      try {
        const data = await apiJson<StoryboardDetail>(
          `/api/v1/storyboards/${storyboard.id}`,
        )
        if (cancelled) return
        setDetail(data)
        setError(null)
      } catch (err) {
        if (cancelled) return
        setError((err as Error).message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
      // Reset loading on cleanup so reopening the modal quickly doesn't
      // briefly show a stale spinner from the previous open cycle.
      setLoading(false)
    }
  }, [open, storyboard])

  const handleDelete = async () => {
    if (!storyboard) return
    if (
      !window.confirm(
        'Delete this storyboard? The rendered image and any linked panels will stay in your Library — only the storyboard group is removed.',
      )
    ) {
      return
    }
    setDeleting(true)
    try {
      const res = await apiFetch(`/api/v1/storyboards/${storyboard.id}`, {
        method: 'DELETE',
      })
      if (!res.ok && res.status !== 204) {
        throw new Error(`Delete failed (${res.status})`)
      }
      onDeleted()
      onOpenChange(false)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setDeleting(false)
    }
  }

  // Pull the plan's panel list out for display. Defensively typed — the
  // plan blob is loosely shaped by the LLM, so we narrow rather than trust.
  const panels = React.useMemo<Array<Record<string, unknown>>>(() => {
    const raw = detail?.plan?.panels
    return Array.isArray(raw) ? (raw as Array<Record<string, unknown>>) : []
  }, [detail])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        {storyboard && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg">
                {storyboard.title || 'Untitled storyboard'}
                {storyboard.panel_count != null && (
                  <Badge variant="secondary" className="text-[10px]">
                    {storyboard.panel_count} panels
                  </Badge>
                )}
              </DialogTitle>
              {storyboard.brief && (
                <DialogDescription className="line-clamp-3">
                  {storyboard.brief}
                </DialogDescription>
              )}
            </DialogHeader>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : error ? (
              <p className="text-sm text-destructive py-4">{error}</p>
            ) : (
              <div className="space-y-5">
                {/* Primary rendered image — the main artefact users care about */}
                {storyboard.primary_asset && (
                  <div className="rounded-lg overflow-hidden border border-border bg-muted">
                    <img
                      src={`/uploads/${storyboard.primary_asset.file_path}`}
                      alt={storyboard.title ?? 'Storyboard'}
                      className="w-full h-auto"
                    />
                  </div>
                )}

                {/* Panel breakdown from the plan, if available */}
                {panels.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium">Panels</h3>
                    <ol className="space-y-2">
                      {panels.map((p, i) => {
                        const title = typeof p.title === 'string' ? p.title : null
                        const description =
                          typeof p.description === 'string' ? p.description : null
                        const beat = typeof p.beat === 'string' ? p.beat : null
                        return (
                          <li
                            key={i}
                            className="rounded-md border border-border p-2.5 text-xs space-y-1"
                          >
                            <p className="font-medium">
                              {i + 1}. {title || `Panel ${i + 1}`}
                            </p>
                            {beat && (
                              <p className="text-muted-foreground italic">{beat}</p>
                            )}
                            {description && (
                              <p className="text-muted-foreground">{description}</p>
                            )}
                          </li>
                        )
                      })}
                    </ol>
                  </div>
                )}

                {/* Linked assets — usually just the primary; future-extension
                    for derivative scene clips */}
                {detail && detail.assets.length > 1 && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium">
                      Linked assets ({detail.assets.length})
                    </h3>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {detail.assets.map((a) => (
                        <div
                          key={a.id}
                          className="rounded-md overflow-hidden border border-border aspect-video bg-muted"
                          title={a.original_filename ?? a.filename}
                        >
                          {a.file_type === 'image' ? (
                            <img
                              src={`/uploads/${a.file_path}`}
                              alt={a.filename}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                              <ImageIcon className="h-6 w-6 opacity-40" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Footer actions */}
                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:text-destructive gap-1"
                    onClick={handleDelete}
                    disabled={deleting}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    {deleting ? 'Deleting...' : 'Delete storyboard'}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => onUseInCreate(storyboard)}
                    className="gap-1"
                  >
                    Use in Create Ad
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
